/** Desktop handoff exposes only bounded text and allowlisted navigation metadata. */
export type DesktopNavigation = {
    screen: 'data-search' | 'ai-model';
    tab?: 'equipment' | 'data';
    query?: string;
    process?: string;
};

const completedStates = new Set(['completed', 'success', 'done', 'ok']);
const failedStates = new Set(['failed', 'error', 'blocked', 'cancelled']);
const modelTools = new Set([
    'model.predict', 'model.run', 'model.train', 'model.select',
    'alsphere_catalog', 'alsphere_process_options', 'alsphere_model_detail',
    'alsphere_deposition_predict', 'alsphere_etch_predict', 'alsphere_photo_predict',
    'alsphere_doping_predict', 'alsphere_upload_training_csv', 'alsphere_training_file',
    'alsphere_train_uploaded_data', 'alsphere_model_status'
]);
const processNames: { [name: string]: string } = {
    photo: 'photo', photolithography: 'photo', lithography: 'photo', 포토: 'photo', 노광: 'photo',
    etch: 'etch', dryetch: 'etch', etching: 'etch', 식각: 'etch',
    deposition: 'deposition', 증착: 'deposition',
    ionimplnt: 'ionimplnt', ionimplant: 'ionimplnt', ionimplantation: 'ionimplnt',
    doping: 'ionimplnt', 이온주입: 'ionimplnt',
    abnrmdtctn: 'abnrmdtctn', anomalydetection: 'abnrmdtctn', 이상탐지: 'abnrmdtctn',
    wfrdtctnclsfc: 'wfrdtctnclsfc', waferdefectclassification: 'wfrdtctnclsfc',
    waferdefect: 'wfrdtctnclsfc', 웨이퍼결함분류: 'wfrdtctnclsfc'
};

function object(value: any): any {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}
function text(value: any, limit: number): string {
    return typeof value === 'string' ? value.trim().slice(0, limit) : '';
}
function processName(value: any): string {
    if (typeof value !== 'string' || value.length > 120) return '';
    const name = value.toLowerCase().trim().replace(/\s*\([^)]*\)\s*/g, '').replace(/[\s_-]+/g, '');
    return processNames[name] || '';
}
function chosenProcess(result: any, actions: any[]): string {
    const analysis = object(result.analysis);
    const request = object(result.executionRequest || analysis.executionRequest);
    const planning = object(request.alspherePlanning);
    const entities = object(request.entities || analysis.entities);
    const values: any[] = [
        result.execution?.process, planning.process, planning.processId,
        result.model?.process, result.model?.process_id, result.process, result.processId,
        result.modelResult?.process, result.modelResult?.model?.process_id
    ];
    for (const action of actions.filter(item => modelTools.has(item?.name))) {
        for (const container of [action.arguments, action.params, action.result, action.output]) {
            values.push(container?.process, container?.process_id, container?.processId,
                container?.model?.process, container?.model?.process_id);
        }
    }
    for (const value of values) {
        const process = processName(value);
        if (process) return process;
    }
    const scoped = new Set([entities.process, ...(Array.isArray(entities.processes) ? entities.processes : [])]
        .map(processName).filter(Boolean));
    return scoped.size === 1 ? [...scoped][0] : '';
}
/** Resolve scoped provider identifiers only against names in the returned table. */
function scopedSearchTerm(value: string, kind: 'equipment' | 'processes', result: any, resource: any): string {
    const table = object(result.table);
    const columns = Array.isArray(table.columns) ? table.columns : [];
    const identifierLabels = kind === 'equipment' ? ['장비 식별번호', '장비 ID'] : ['대표공정 식별번호', '공정 식별번호'];
    const nameLabels = kind === 'equipment' ? ['국문명', '영문명', '장비명']
        : columns.includes('대표공정') ? ['대표공정'] : ['국문명', '영문명', '공정명'];
    const indexes = identifierLabels.map(label => columns.indexOf(label)).filter(index => index >= 0);
    const rows = Array.isArray(table.rows) ? table.rows.slice(0, 100) : [];
    const matched = rows.find(row => Array.isArray(row) && indexes.some(index => String(row[index] ?? '') === value));
    if (matched) {
        for (const label of nameLabels) {
            const name = text(matched[columns.indexOf(label)], 120);
            if (name) return name;
        }
    }
    const identifier = kind === 'equipment' ? resource.apiParams?.eqpSeq : resource.apiParams?.procNo;
    // An internal provider ID is not a meaningful free-text search term.
    if (String(identifier ?? '') === value || /^\d+$/.test(value) || /^[0-9a-f]{24,}$/i.test(value)) return '';
    return value;
}
function dataSearchQuery(result: any, request: any, analysis: any, question: string): string {
    const entities = object(request.entities || analysis.entities);
    const resource = object(request.resource);
    for (const kind of ['equipment', 'processes'] as const) {
        const values = Array.isArray(entities[kind]) ? entities[kind].slice(0, 8) : [entities[kind]];
        const terms = [...new Set(values.map(value => text(value, 120)).filter(Boolean)
            .map(value => scopedSearchTerm(value, kind, result, resource)).filter(Boolean))];
        if (terms.length) return terms.join(' ').slice(0, 240);
    }
    const providerQuery = text(resource.searchArguments?.inputSearch?.value, 240);
    if (providerQuery) return providerQuery;
    const ignored = new Set([
        '공정별장비목록', '장비목록', '공정목록', '대표공정목록', '세부공정목록', '속성목록',
        '장비', '공정', '데이터', '검색', '조회', '목록', 'equipment', 'process', 'data', 'search', 'list'
    ]);
    const terms = (Array.isArray(request.searchTerms) ? request.searchTerms.slice(0, 16) : [])
        .map(value => text(value, 120)).filter(value => value && !ignored.has(value.toLowerCase().replace(/\s+/g, '')));
    if (terms.length) return [...new Set(terms)].join(' ').slice(0, 240);
    return text(analysis.resolvedQuery || result.resolvedQuery || question, 240);
}

function navigationFor(result: any, question: string): DesktopNavigation | null {
    const analysis = object(result.analysis);
    const request = object(result.executionRequest || analysis.executionRequest);
    const actions = [
        ...(Array.isArray(result.actions) ? result.actions.slice(0, 100) : []),
        ...(Array.isArray(result.tools) ? result.tools.slice(0, 100) : [])
    ].filter(item => item && typeof item === 'object' && typeof item.name === 'string');
    if (actions.some(item => failedStates.has(text(item.status, 30).toLowerCase())
        || item.recoveryStatus === 'fallback_completed') || result.llm?.fallback === true) return null;
    const completed = actions.filter(item => completedStates.has(text(item.status, 30).toLowerCase()));
    const intentValue = result.intent || analysis.intent || request.intent?.primary;
    const intent = text(typeof intentValue === 'object' ? intentValue?.primary : intentValue, 80).toLowerCase();
    const mode = text(result.resolvedAgentMode || analysis.resolvedAgentMode || result.agentMode || analysis.agentMode, 80);
    const source = text(result.source, 100).toLowerCase();
    const alsphereWorkflow = result.handled === true
        && /^alsphere(?:_|$)/.test(text(result.execution?.agentId, 80));
    const modelIntent = ['predict', 'train', 'model', 'ai_model', 'anomaly', 'optimize'].includes(intent)
        || mode === 'ai_model' || analysis.requiresModel === true || alsphereWorkflow;
    if (modelIntent && completed.some(item => modelTools.has(item.name))
        && (alsphereWorkflow || completed.some(item => item.name.startsWith('model.'))
            || ['alsphere-api', 'alsphere api'].includes(source))) {
        const process = chosenProcess(result, completed);
        return { screen: 'ai-model', ...(process ? { process } : {}) };
    }
    const provider = text(result.searchProvider || analysis.searchProvider || request.resource?.provider, 80).toLowerCase();
    const bandiSource = ['bandi-api', 'bandi api', 'bandi process data api'].includes(source);
    const dataScope = text(result.dataScope, 80).toLowerCase();
    if ((intent === 'search' || mode === 'data_search') && (provider === 'bandi' || bandiSource)
        && completed.some(item => ['data.search', 'dataset.search'].includes(item.name))
        && !['sample', 'no_data', 'unavailable'].includes(dataScope)) {
        const query = dataSearchQuery(result, request, analysis, question);
        const resource = object(request.resource);
        const tab: DesktopNavigation['tab'] = resource.apiTarget === 'process_equipment'
            || resource.type === 'equipment' || result.searchResourceType === 'equipment' ? 'equipment'
            : resource.type === 'data' ? 'data' : undefined;
        return { screen: 'data-search', ...(query ? { query } : {}), ...(tab ? { tab } : {}) };
    }
    return null;
}

/** Uses execution metadata, never question keywords, to select a BANDI screen. */
export function publicDesktopChatCompletion(event: any) {
    if (!event || typeof event.question !== 'string' || typeof event.requestId !== 'string'
        || !/^req-[0-9a-f]{32}$/.test(event.requestId)) return null;
    const result = object(event.result);
    if (result.error || failedStates.has(text(result.status, 30).toLowerCase())
        || result.llm?.synthesisStatus === 'failed') return null;
    const answer = result.answer || result.content || result.reply;
    if (typeof answer !== 'string' || !answer.trim()) return null;
    const navigation = navigationFor(result, event.question);
    return {
        requestId: event.requestId, question: event.question.slice(0, 2000), answer: answer.slice(0, 8000),
        ...(navigation ? { navigation } : {})
    };
}
